/**
 * Phase 1 notification events — map worker/task transitions to sound intents.
 *
 * Pure: previous snapshot + next snapshot → events. The playback engine turns
 * those into queued token ids / SFX. Visual notices remain independent.
 */

import {
  workerNumberTokenId,
  type ResolvedNotificationAsset,
} from './notification-audio-pack';
import {
  effectiveEventMode,
  type EventSoundMode,
  type NotificationEventKey,
  type NotificationSeverityKey,
  type NotificationSoundSettings,
  type SeverityBehavior,
} from './notification-sound-settings';

export type NotificationEventKind =
  | 'task_finished'
  | 'task_failed'
  | 'needs_permission'
  | 'worker_offline'
  | 'worker_online';

export interface NotificationWorkerSnapshot {
  readonly workerId: string;
  readonly workerNumber: number;
  readonly status: string;
  /** Pending approvals / waiting_for_approval / waiting_for_input. */
  readonly needsPermission: boolean;
}

export interface NotificationSoundEvent {
  readonly kind: NotificationEventKind;
  readonly workerId: string;
  readonly workerNumber: number;
  readonly eventKey: NotificationEventKey;
  readonly severity: NotificationSeverityKey;
  /** Stable key for debounce (kind + workerId). */
  readonly debounceKey: string;
}

const EVENT_KEY: Record<NotificationEventKind, NotificationEventKey> = {
  task_finished: 'taskFinished',
  task_failed: 'taskFailed',
  needs_permission: 'needsPermission',
  worker_offline: 'workerOffline',
  worker_online: 'workerOnline',
};

const EVENT_SEVERITY: Record<NotificationEventKind, NotificationSeverityKey> = {
  task_finished: 'routine',
  task_failed: 'needsAttention',
  needs_permission: 'needsAttention',
  worker_offline: 'important',
  worker_online: 'routine',
};

const FINISHED_STATUSES = new Set(['completed']);
const FAILED_STATUSES = new Set(['failed']);
const OFFLINE_STATUSES = new Set(['offline']);
const ONLINE_FROM = new Set(['offline']);

function normalizeStatus(status: string): string {
  return status.trim().toLowerCase();
}

/**
 * Diff two worker snapshots and emit Phase 1 sound events.
 * First observation (prev empty) does not fire — avoids a fanfare on load.
 */
export function detectNotificationEvents(
  previous: readonly NotificationWorkerSnapshot[],
  next: readonly NotificationWorkerSnapshot[],
): readonly NotificationSoundEvent[] {
  if (previous.length === 0) return [];
  const prevById = new Map(previous.map((row) => [row.workerId, row]));
  const events: NotificationSoundEvent[] = [];

  for (const row of next) {
    const prev = prevById.get(row.workerId);
    if (!prev) continue;
    const was = normalizeStatus(prev.status);
    const now = normalizeStatus(row.status);

    if (was !== now) {
      if (FINISHED_STATUSES.has(now)) {
        events.push(makeEvent('task_finished', row));
      } else if (FAILED_STATUSES.has(now)) {
        events.push(makeEvent('task_failed', row));
      } else if (OFFLINE_STATUSES.has(now)) {
        events.push(makeEvent('worker_offline', row));
      } else if (ONLINE_FROM.has(was) && !OFFLINE_STATUSES.has(now)) {
        events.push(makeEvent('worker_online', row));
      }
    }

    if (!prev.needsPermission && row.needsPermission) {
      events.push(makeEvent('needs_permission', row));
    }
  }

  return events;
}

function makeEvent(kind: NotificationEventKind, row: NotificationWorkerSnapshot): NotificationSoundEvent {
  return {
    kind,
    workerId: row.workerId,
    workerNumber: row.workerNumber,
    eventKey: EVENT_KEY[kind],
    severity: EVENT_SEVERITY[kind],
    debounceKey: `${kind}:${row.workerId}`,
  };
}

export interface PlaybackPlan {
  readonly mode: EventSoundMode;
  readonly severity: NotificationSeverityKey;
  readonly critical: boolean;
  /** Ordered token / sfx ids to play (manifest ids). Empty when off. */
  readonly tokenIds: readonly string[];
  readonly fallbackSfxId: string | null;
}

const PHRASE_FOR_KIND: Record<NotificationEventKind, string> = {
  task_finished: 'phrase-finished',
  task_failed: 'phrase-encountered-a-problem',
  needs_permission: 'phrase-needs-your-permission',
  worker_offline: 'phrase-is-offline',
  worker_online: 'phrase-is-back-online',
};

const BEEP_FOR_KIND: Record<NotificationEventKind, string> = {
  task_finished: 'sfx-routine-chime',
  task_failed: 'sfx-attention',
  needs_permission: 'sfx-attention',
  worker_offline: 'sfx-attention',
  worker_online: 'sfx-routine-chime',
};

function severityAllows(
  behavior: SeverityBehavior,
  mode: EventSoundMode,
): EventSoundMode {
  if (behavior === 'off') return 'off';
  if (mode === 'off') return 'off';
  if (mode === 'beep') {
    if (behavior === 'voice') return 'voice';
    return 'beep';
  }
  // mode === voice
  if (behavior === 'beep') return 'beep';
  if (behavior === 'beep_and_voice') return 'voice'; // voice path may prepend beep separately
  return 'voice';
}

/**
 * Build the token/SFX plan for one sound event under current settings.
 * Pure — no I/O. Missing number tokens leave voice incomplete (caller falls back).
 */
export function planNotificationPlayback(
  event: NotificationSoundEvent,
  settings: NotificationSoundSettings,
): PlaybackPlan {
  const critical = event.severity === 'critical';
  let mode = effectiveEventMode(settings, event.eventKey);
  mode = severityAllows(settings.severity[event.severity], mode);

  const beepId = BEEP_FOR_KIND[event.kind];
  const phraseId = PHRASE_FOR_KIND[event.kind];
  const numberId = workerNumberTokenId(event.workerNumber);

  if (mode === 'off') {
    return { mode, severity: event.severity, critical, tokenIds: [], fallbackSfxId: beepId };
  }

  if (mode === 'beep') {
    return { mode, severity: event.severity, critical, tokenIds: [beepId], fallbackSfxId: beepId };
  }

  // voice
  const tokens: string[] = [];
  const severity = settings.severity[event.severity];
  if (severity === 'beep_and_voice' || event.kind === 'task_failed') {
    // Optional leading attention for failure / critical-ish severity.
    if (severity === 'beep_and_voice') tokens.push(beepId);
  }
  tokens.push('token-worker');
  if (numberId !== null) tokens.push(numberId);
  tokens.push(phraseId);

  return {
    mode,
    severity: event.severity,
    critical,
    tokenIds: tokens,
    fallbackSfxId: beepId,
  };
}

/** Compose Worker + number + phrase for join proofs / previews. */
export function composeWorkerPhraseTokens(
  workerNumber: number,
  phraseId: string,
): readonly string[] {
  const numberId = workerNumberTokenId(workerNumber);
  const tokens = ['token-worker'];
  if (numberId !== null) tokens.push(numberId);
  tokens.push(phraseId);
  return tokens;
}

/** Required join proof sequences from #292. */
export const JOIN_PROOF_SEQUENCES: readonly { readonly id: string; readonly tokens: readonly string[] }[] = [
  { id: 'join-worker-one-finished', tokens: composeWorkerPhraseTokens(1, 'phrase-finished') },
  { id: 'join-worker-two-problem', tokens: composeWorkerPhraseTokens(2, 'phrase-encountered-a-problem') },
  { id: 'join-worker-three-permission', tokens: composeWorkerPhraseTokens(3, 'phrase-needs-your-permission') },
  { id: 'join-worker-four-offline', tokens: composeWorkerPhraseTokens(4, 'phrase-is-offline') },
  { id: 'join-worker-twelve-finished', tokens: composeWorkerPhraseTokens(12, 'phrase-finished') },
  { id: 'join-worker-seventeen-permission', tokens: composeWorkerPhraseTokens(17, 'phrase-needs-your-permission') },
];

/** Drop unresolved tokens; if voice sequence collapses, fall back to SFX id. */
export function materializePlanUrls(
  plan: PlaybackPlan,
  assets: ReadonlyMap<string, ResolvedNotificationAsset>,
): { readonly urls: readonly string[]; readonly usedFallback: boolean } {
  if (plan.mode === 'off') return { urls: [], usedFallback: false };
  const urls: string[] = [];
  for (const id of plan.tokenIds) {
    const asset = assets.get(id);
    if (asset) urls.push(asset.url);
  }
  if (urls.length > 0) return { urls, usedFallback: false };
  if (plan.fallbackSfxId) {
    const fallback = assets.get(plan.fallbackSfxId);
    if (fallback) return { urls: [fallback.url], usedFallback: true };
  }
  return { urls: [], usedFallback: true };
}
